import React, { useEffect, useState } from 'react';
import styles from '../../../../assets/styles/recentActivity.module.css';
import style from '../../../../assets/styles/commentSection.module.css';
import RecentActivityIcon from '../../../../assets/images/recentActivity.svg';
import ClockIcon from '../../../../assets/images/clockIcon.svg';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { flushTaskViewData, recentActivity } from '../../../../redux/action';

const RecentActivity = () => {
  const [loading, setLoading] = useState(true);
  const organizationId = localStorage.getItem('selectedOrgId');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id: currentTaskId } = useParams(); // Get current task ID from URL
  const { recentActivityList } = useSelector((state: any) => state.GetAllData);
  const me = JSON.parse(localStorage.getItem('userInfo') ?? '{}');
  useEffect(() => {
    dispatch(recentActivity(organizationId));
  }, [organizationId]);

  useEffect(() => {
    if (recentActivityList?.data || recentActivityList?.error) {
      setLoading(false);
    }
  }, [recentActivityList]);

  const getActivityMessage = (
    task_status_id: number,
    assignee: string,
    approvers: string[],
    taskId: string,
    is_first: boolean,
    user_id: number
  ) => {
    const statusMap: Record<number, string> = {
      1: 'To-Do',
      2: 'In Process',
      3: 'In Review',
      4: 'Approved',
      5: 'Posted Comment',
      6: 'Assignee Modify',
      7: 'Approver Modify',
    };

    const mappedStatus = statusMap[task_status_id] || 'Unknown';
    const normalizedStatus = mappedStatus.toLowerCase();

    const moveStatuses = ['approved', 'in review'];
    const actionTemplates: Record<string, JSX.Element> = {
      'to-do': (
        <>
          {!is_first ? (
            <>
              <span className={style.commentByClass}>has moved task</span>
              <span> {mappedStatus}</span>
              <span className={style.commentByClass}>for</span>
              <span className={styles.activityLink}>ID {taskId}</span>
            </>
          ) : (
            <>
              {assignee && (
                <p>
                  <span className={style.userName}>{assignee}</span>
                  <span className={style.commentByClass}> is the Assignee for </span>
                  <span className={styles.activityLink}>ID {taskId}</span>
                </p>
              )}
              {approvers.length > 0 && (
                <p>
                  <span className={style.userName}>{approvers.join(' and ')}</span>
                  <span className={style.commentByClass}>
                    {approvers.length === 1 ? ' has' : ' have'} been added as approver
                    {approvers.length > 1 ? 's' : ''} to
                  </span>
                  <span className={styles.activityLink}> ID {taskId}</span>
                </p>
              )}
            </>
          )}
        </>
      ),
      'in process': (
        <>
          <span className={style.commentByClass}>sent</span>
          <span className={styles.activityLink}>ID {taskId}</span>
          <span className={style.commentByClass}>to</span>
          <span>{mappedStatus}</span>
        </>
      ),
      'posted comment': (
        <>
          <span className={style.commentByClass}>has commented on</span>
          <span className={styles.activityLink}>ID {taskId}</span>
        </>
      ),
      'assignee modify': (
        <>
          <span className={style.commentByClass}>has been assigned</span>
          <span className={styles.activityLink}>ID {taskId}</span>
          <span className={style.commentByClass}>as</span>
          <span className={styles.userName}>Assignee</span>
        </>
      ),
      'approver modify': (
        <>
          <span className={style.commentByClass}>has been assigned</span>
          <span className={styles.activityLink}>ID {taskId}</span>
          <span className={style.commentByClass}>as</span>
          <span className={styles.userName}>Approver</span>
        </>
      ),
    };

    if (moveStatuses.includes(normalizedStatus)) {
      return (
        <>
          <span className={style.commentByClass}>has moved</span>
          <span className={styles.activityLink}>ID {taskId}</span>
          <span className={style.commentByClass}>to</span>
          <span>{mappedStatus}</span>
        </>
      );
    }

    return actionTemplates[normalizedStatus] || null; // Fallback for unknown statuses
  };

  const formatCommentTime = (timestamp: any) => {
    if (!timestamp || typeof timestamp !== 'string') return 'Invalid Date';

    const parseISODate = (ts: any) => new Date(ts);
    const parseCustomDate = (ts: any) => {
      const parts = ts.split(' | ');
      if (parts.length !== 2) return null;

      const [datePart, timePart] = parts;
      const dateParts = datePart.split('/').map(Number);
      if (dateParts.length !== 3) return null;

      const [month, day, year] = dateParts;
      if (!/am|pm/.test(timePart)) return null;

      const [time, modifier] = timePart.split(' ');
      const [hours, minutes] = time.split(':').map(Number);
      let adjustedHours;
      if (modifier === 'pm' && hours !== 12) {
        adjustedHours = hours + 12;
      } else if (modifier === 'am' && hours === 12) {
        adjustedHours = 0;
      } else {
        adjustedHours = hours;
      }

      return new Date(year, month - 1, day, adjustedHours, minutes);
    };

    const commentDate = timestamp.includes('T') ? parseISODate(timestamp) : parseCustomDate(timestamp);
    if (!commentDate || isNaN(commentDate.getTime())) return 'Invalid Date';

    const now = new Date();
    const isSameDay = now.toDateString() === commentDate.toDateString();

    const displayHours = commentDate.getHours() % 12 || 12;
    const displayMinutes = String(commentDate.getMinutes()).padStart(2, '0');
    const ampm = commentDate.getHours() >= 12 ? 'pm' : 'am';
    const formattedTime = `${displayHours}:${displayMinutes} ${ampm}`;

    if (isSameDay) return `Today at ${formattedTime}`;

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${commentDate.getDate()} ${monthNames[commentDate.getMonth()]} ${commentDate.getFullYear()}, ${formattedTime}`;
  };

  const handleActivityClick = async (taskId: string) => {
    // Don't do anything if clicking the current task
    if (taskId === currentTaskId) {
      return;
    }
    try {
      dispatch(flushTaskViewData());
      // Force a small delay to ensure state is cleared before navigate
      await new Promise((resolve) => setTimeout(resolve, 50));
      navigate(`/task-management/details/${taskId}`);
    } catch (error) {
      console.log('Error occured while navigating to task:', error);
    }
  };
  return (
    <div className={styles.activityContainer}>
      <div className={styles.header}>
        <span className={styles.icon}>
          <img src={RecentActivityIcon} alt="Recent Activity Icon" />
        </span>
        <span className={styles.label}>Recent Activity</span>
      </div>
      <div className={styles.activityList}>
        {loading && <p className={styles.loadingClass}>Loading...</p>}

        {!loading && recentActivityList?.data?.length === 0 && (
          <p className={styles.noDataMessage}>No recent activities found.</p>
        )}

        {!loading &&
          recentActivityList?.data?.map((activity: any) => (
            <React.Fragment key={activity.id}>
              {/* Activity Button */}
              <button
                className={styles.activityItem}
                type="button"
                onClick={() => handleActivityClick(activity.task_id)}
              >
                {activity.task_status_id !== 8 ? (
                  <div className={styles.activityContent}>
                    {/* Do not display activity type if status is "To-Do" */}
                    {!(activity.task_status_id === 1 && activity.is_first) && (
                      <p className={styles.activityType}>{activity.task_status}</p>
                    )}

                    <p className={styles.activityDetails}>
                      {!(activity.task_status_id === 1 && activity.is_first) && (
                        <span className={styles.userName}>{activity.user_name}</span>
                      )}

                      <span className={`${styles.taskStatus} ${styles[activity.task_status.toLowerCase()]}`}>
                        {getActivityMessage(
                          activity.task_status_id,
                          activity.assignee,
                          activity.approvers,
                          activity.task_id,
                          activity.is_first,
                          activity.user_id
                        )}
                      </span>
                    </p>
                  </div>
                ) : (
                  <div className={styles.activityContent}>
                    {/* Do not display activity type if status is "To-Do" */}
                    {!(activity.task_status_id === 1 && activity.is_first) && (
                      <p className={styles.activityType}>{activity.task_status}</p>
                    )}

                    <p className={styles.activityDetails}>
                      <span className={styles.userName}>{activity?.tagged_by?.name}</span>

                      <span className={`${styles.taskStatus} ${styles[activity.task_status.toLowerCase()]}`}>
                        <span className={style.commentByClass}>
                          {`has tagged ${me.id === activity.tagged_user.user_id ? 'you' : activity.tagged_user.user_name} in`}
                        </span>
                        <span className={styles.activityLink}>ID {activity.task_id}</span>
                      </span>
                    </p>
                  </div>
                )}
                <p className={styles.timestamp}>
                  <span className={styles.clockIcon}>
                    <img src={ClockIcon} alt="Clock icon" />
                  </span>
                  {formatCommentTime(activity.created_at)}
                </p>
              </button>
            </React.Fragment>
          ))}
      </div>
    </div>
  );
};

export default RecentActivity;
